'use client';

import { useCallback, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import type { JsonValue } from '@lotushealth/lotushealth_js_sdk/schemas';

import { Button } from '@/shared/ui/shadcn/button';
import { Label } from '@/shared/ui/shadcn/label';
import { Input } from '@/shared/ui/shadcn/input';
import { Textarea } from '@/shared/ui/shadcn/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/shadcn/select';
import { useCopilotOverlays } from '@/shared/stores/ui';
import { useProposedActionMutations } from '@/features/proposed-actions-plan/model/useProposedActionMutations';
import { useRxWizardForm, DetailsStep } from '@/features/rx-form';
import type { SourceProposedActionT } from '@/shared/lib';
import { logger } from '@/shared/lib';
import type { DosespotActionPrefill } from '../model/dosespotActionPrefill';
import { buildRxChangeApproveRequest } from '../model/buildRxChangeApproveRequest';
import {
  submitRefillAction,
  submitRxChangeAction,
  type RefillActionRequest,
  type RxChangeActionRequest,
} from '../api/dosespotActionApi';

type ActionMode = 'approve' | 'deny' | 'replace' | 'approve_with_changes';

const REFILL_DENIAL_REASONS = [
  { value: 'DeniedRefillInappropriate', label: 'Refill Inappropriate' },
  { value: 'DeniedPatientUnknown', label: 'Patient Unknown' },
  { value: 'DeniedPatientNotUnderCare', label: 'Patient Not Under Care' },
  { value: 'DeniedNeedAppointment', label: 'Need Appointment' },
  { value: 'DeniedAlreadyPickedUp', label: 'Already Picked Up' },
  { value: 'DeniedNotPickedUp', label: 'Not Picked Up' },
  { value: 'DeniedChangeInappropriate', label: 'Change Inappropriate' },
] as const;

const RXCHANGE_DENIAL_REASONS = [
  { value: 'DeniedChangeInappropriate', label: 'Change Inappropriate' },
  { value: 'DeniedPatientUnknown', label: 'Patient Unknown' },
  { value: 'DeniedPatientNotUnderCare', label: 'Patient Not Under Care' },
  { value: 'DeniedNeedAppointment', label: 'Need Appointment' },
  { value: 'DeniedNeverPrescribed', label: 'Never Prescribed' },
  { value: 'DeniedHavePatientContact', label: 'Have Patient Contact' },
  { value: 'DeniedAlreadyHandled', label: 'Already Handled' },
  { value: 'DeniedAtPatientRequest', label: 'At Patient Request' },
] as const;

function isRefillTool(toolName: string): boolean {
  return (
    toolName === 'approve_refill' || toolName === 'deny_refill' || toolName === 'replace_refill'
  );
}

function defaultModeForTool(toolName: string): ActionMode {
  if (toolName.includes('deny')) {
    return 'deny';
  }
  if (toolName.includes('replace')) {
    return 'replace';
  }
  if (
    toolName.includes('approve_with_changes') ||
    toolName.includes('approve_rxchange_with_changes')
  ) {
    return 'approve_with_changes';
  }
  return 'approve';
}

function isPrescriptionMode(mode: ActionMode): boolean {
  return mode === 'replace' || mode === 'approve_with_changes';
}

function toJsonValueRecord(value: Record<string, unknown>): Record<string, JsonValue> {
  const result: Record<string, JsonValue> = {};
  Object.entries(value).forEach(([key, entryValue]) => {
    if (entryValue !== null && entryValue !== undefined) {
      result[key] = entryValue as JsonValue;
    }
  });
  return result;
}

const MODE_LABELS: Record<ActionMode, string> = {
  approve: 'Approve',
  deny: 'Deny',
  replace: 'Replace',
  approve_with_changes: 'Approve w/ Changes',
};

interface DosespotActionWizardProps {
  prefill: DosespotActionPrefill;
}

export function DosespotActionWizard({ prefill }: DosespotActionWizardProps) {
  const mutations = useProposedActionMutations();
  const overlays = useCopilotOverlays();
  const action = prefill.action;
  const toolName = action.payload.tool_name;

  const sourceProposedActionRef = useRef<SourceProposedActionT | null>(
    prefill.sourceProposedAction ?? { externalId: action.external_id }
  );

  const [mode, setMode] = useState<ActionMode>(() => defaultModeForTool(toolName));
  const [submitting, setSubmitting] = useState(false);

  // Refill approve fields. Empty default forces an explicit count; DoseSpot
  // rejects anything outside 1-99.
  const [refills, setRefills] = useState('');
  const [approveComment, setApproveComment] = useState('');
  const [effectiveDate, setEffectiveDate] = useState('');

  // Deny fields
  const [denialReason, setDenialReason] = useState(() =>
    isRefillTool(toolName) ? 'DeniedRefillInappropriate' : 'DeniedChangeInappropriate'
  );
  const [denyNote, setDenyNote] = useState('');

  // RxChange approve fields
  const [rxChangeComment, setRxChangeComment] = useState('');
  const [priorAuthNumber, setPriorAuthNumber] = useState('');

  // Inline prescription form for replace / approve-with-changes
  const { form: rxForm, buildRequest } = useRxWizardForm();

  const toolInput = useMemo(() => action.payload.tool_input, [action]);
  const isRefill = isRefillTool(toolName);

  const refillId = useMemo(() => {
    const v = toolInput.refillId;
    return typeof v === 'number' ? v : 0;
  }, [toolInput]);

  const rxChangeId = useMemo(() => {
    const v = toolInput.rxChangeId;
    return typeof v === 'number' ? v : 0;
  }, [toolInput]);

  const patientUserId = useMemo(() => {
    // patientUserId lives in the replace_refill / approve_rxchange_with_changes
    // payload (or alternative), not necessarily the primary payload.
    const allPayloads = [action.payload, ...action.alternatives];
    for (const p of allPayloads) {
      const v = p.tool_input.patientUserId;
      if (typeof v === 'string' && v) {
        return v;
      }
    }
    return '';
  }, [action]);

  const handleSubmit = useCallback(async () => {
    const source = sourceProposedActionRef.current;
    if (!source) {
      return;
    }

    setSubmitting(true);
    try {
      let toolOutput: Record<string, JsonValue> = {};

      if (isPrescriptionMode(mode)) {
        if (!patientUserId) {
          toast.error('Patient information is missing for this action.');
          return;
        }

        const result = buildRequest();
        if (!result.success) {
          const firstIssue = result.error.issues[0];
          toast.error(
            `Prescription form error: ${firstIssue?.message ?? 'Please fill in all required fields.'}`
          );
          return;
        }

        const rxRequest = result.data;

        if (isRefill) {
          if (!('DispensableDrugId' in rxRequest)) {
            toast.error('Replace requires a coded prescription.');
            return;
          }
          const req: RefillActionRequest = {
            action: 'replace',
            patient_user_id: patientUserId,
            new_prescription: rxRequest,
          };
          await submitRefillAction(refillId, req);
          toolOutput = { refillId, action: 'replace', patientUserId };
        } else {
          if (!('DispensableDrugId' in rxRequest)) {
            toast.error('Approve with changes requires a coded prescription.');
            return;
          }
          const req: RxChangeActionRequest = {
            action: 'approve_with_changes',
            patient_user_id: patientUserId,
            new_prescription: rxRequest,
            comment: rxChangeComment || null,
          };
          await submitRxChangeAction(rxChangeId, req);
          toolOutput = { rxChangeId, action: 'approve_with_changes', patientUserId };
        }
      } else if (isRefill) {
        let req: RefillActionRequest;
        switch (mode) {
          case 'approve': {
            const refillCount = parseInt(refills, 10);
            if (!Number.isInteger(refillCount) || refillCount < 1 || refillCount > 99) {
              toast.error('Refills must be a whole number between 1 and 99.');
              return;
            }
            req = {
              action: 'approve',
              refills: refillCount,
              comment: approveComment || null,
              effective_date: effectiveDate || null,
            };
            break;
          }
          case 'deny':
            req = {
              action: 'deny',
              denial_reason: denialReason,
              note: denyNote || null,
            };
            break;
          default:
            return;
        }
        await submitRefillAction(refillId, req);
        toolOutput = toJsonValueRecord({ refillId, ...req });
      } else {
        let req: RxChangeActionRequest;
        switch (mode) {
          case 'approve':
            req = buildRxChangeApproveRequest({
              toolInput,
              comment: rxChangeComment,
              priorAuthNumber,
            });
            break;
          case 'deny':
            req = {
              action: 'deny',
              denial_reason: denialReason,
              comment: denyNote || null,
            };
            break;
          default:
            return;
        }
        await submitRxChangeAction(rxChangeId, req);
        toolOutput = toJsonValueRecord({ rxChangeId, ...req });
      }

      await mutations.finalizeFromSource(source, toolOutput).catch((err: unknown) => {
        logger.error('dosespotAction.finalizeFromSource.failed', {
          externalId: source.externalId,
          err,
        });
      });

      toast.success(
        mode === 'approve'
          ? 'Approved successfully.'
          : mode === 'deny'
            ? 'Denied successfully.'
            : mode === 'replace'
              ? 'Replaced successfully.'
              : 'Approved with changes successfully.'
      );
      overlays.close('dosespotAction');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      toast.error(`Action failed: ${message}`);
      logger.error('dosespotAction.submit.failed', { mode, err });
    } finally {
      setSubmitting(false);
    }
  }, [
    isRefill,
    mode,
    refillId,
    rxChangeId,
    patientUserId,
    refills,
    approveComment,
    effectiveDate,
    denialReason,
    denyNote,
    rxChangeComment,
    priorAuthNumber,
    toolInput,
    buildRequest,
    mutations,
    overlays,
  ]);

  const justification = action.justification;
  const entityLabel = isRefill ? 'Refill' : 'RxChange';
  const availableModes: ActionMode[] = isRefill
    ? ['approve', 'deny', 'replace']
    : ['approve', 'deny', 'approve_with_changes'];

  const submitLabel = submitting ? 'Submitting…' : `${MODE_LABELS[mode]} ${entityLabel}`;

  return (
    <div className="flex h-full flex-col gap-3 overflow-y-auto p-3">
      <div className="text-sm font-semibold">{action.title}</div>

      {justification && !isPrescriptionMode(mode) ? (
        <pre className="max-h-40 overflow-y-auto whitespace-pre-wrap rounded-md bg-muted p-2 text-xs leading-relaxed">
          {justification}
        </pre>
      ) : null}

      <div className="flex flex-wrap gap-1">
        {availableModes.map((m) => (
          <Button
            key={m}
            size="sm"
            variant={mode === m ? 'default' : 'outline'}
            onClick={() => setMode(m)}
            className="text-xs"
          >
            {MODE_LABELS[m]}
          </Button>
        ))}
      </div>

      <div className="flex flex-col gap-2">
        {mode === 'approve' && isRefill ? (
          <>
            <div>
              <Label className="text-xs">Refills</Label>
              <Input
                type="number"
                min={1}
                max={99}
                value={refills}
                placeholder="1-99"
                onChange={(e) => setRefills(e.target.value)}
                className="h-7 text-xs"
              />
            </div>
            <div>
              <Label className="text-xs">Comment (optional)</Label>
              <Textarea
                value={approveComment}
                onChange={(e) => setApproveComment(e.target.value)}
                rows={2}
                className="text-xs"
              />
            </div>
            <div>
              <Label className="text-xs">Effective Date (optional)</Label>
              <Input
                type="date"
                value={effectiveDate}
                onChange={(e) => setEffectiveDate(e.target.value)}
                className="h-7 text-xs"
              />
            </div>
          </>
        ) : null}

        {mode === 'approve' && !isRefill ? (
          <>
            <div>
              <Label className="text-xs">Prior Auth Number (optional)</Label>
              <Input
                value={priorAuthNumber}
                onChange={(e) => setPriorAuthNumber(e.target.value)}
                className="h-7 text-xs"
              />
            </div>
            <div>
              <Label className="text-xs">Comment (optional)</Label>
              <Textarea
                value={rxChangeComment}
                onChange={(e) => setRxChangeComment(e.target.value)}
                rows={2}
                className="text-xs"
              />
            </div>
          </>
        ) : null}

        {mode === 'deny' ? (
          <>
            <div>
              <Label className="text-xs">Denial Reason</Label>
              <Select value={denialReason} onValueChange={setDenialReason}>
                <SelectTrigger className="h-7 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(isRefill ? REFILL_DENIAL_REASONS : RXCHANGE_DENIAL_REASONS).map((r) => (
                    <SelectItem key={r.value} value={r.value} className="text-xs">
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">{isRefill ? 'Note' : 'Comment'} (optional)</Label>
              <Textarea
                value={denyNote}
                onChange={(e) => setDenyNote(e.target.value)}
                rows={2}
                className="text-xs"
              />
            </div>
          </>
        ) : null}

        {isPrescriptionMode(mode) ? (
          <div className="flex flex-col gap-2">
            <div className="rounded-md border border-border bg-muted/50 p-2">
              <p className="mb-2 text-xs font-medium text-muted-foreground">
                {mode === 'replace'
                  ? 'Configure the replacement prescription below.'
                  : 'Configure the modified prescription below.'}
              </p>
              <DetailsStep form={rxForm} userId={patientUserId} />
            </div>
            {!isRefill ? (
              <div>
                <Label className="text-xs">Comment (optional)</Label>
                <Textarea
                  value={rxChangeComment}
                  onChange={(e) => setRxChangeComment(e.target.value)}
                  rows={2}
                  className="text-xs"
                />
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="mt-auto flex justify-end gap-2 pt-2">
        <Button
          size="sm"
          variant="outline"
          onClick={() => overlays.close('dosespotAction')}
          className="text-xs"
        >
          Cancel
        </Button>
        <Button size="sm" onClick={handleSubmit} disabled={submitting} className="text-xs">
          {submitLabel}
        </Button>
      </div>
    </div>
  );
}
